import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchVehicles,
  fetchVehicle,
  updateVehicle,
  createVehicle,
  deleteVehicle,
} from "../api/vehicle";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  count: 0,
  activeCount: 0,
  withDriverCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteVehicleAsync = createAsyncThunk(
  "vehicles/deleteVehicleAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteVehicle(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "delete_vehicle");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_vehicle");
    }
  }
);

export const createVehicleAsync = createAsyncThunk(
  "vehicles/createVehicleAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createVehicle(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_vehicle");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_vehicle");
    }
  }
);

export const updateVehicleAsync = createAsyncThunk(
  "vehicles/updateVehicleAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateVehicle(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_vehicle");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_vehicle");
    }
  }
);

export const getVehiclesAsync = createAsyncThunk(
  "vehicles/getVehiclesAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchVehicles(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_vehicles");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_vehicles");
    }
  }
);

export const getVehicleAsync = createAsyncThunk(
  "vehicles/getVehicleAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchVehicle(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_vehicle");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_vehicle");
    }
  }
);

const vehicleSlice = createSlice({
  name: "vehicles",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getVehiclesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getVehiclesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.count = action.payload.count;
        state.activeCount = action.payload.activeCount;
        state.withDriverCount = action.payload.withDriverCount;
      })
      .addCase(getVehiclesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente kjøretøyene.");
      })
      .addCase(updateVehicleAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateVehicleAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateVehicleAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere kjøretøyet.");
      })
      .addCase(createVehicleAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createVehicleAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createVehicleAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette kjøretøyet.");
      })
      .addCase(deleteVehicleAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteVehicleAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data.splice(index, 1);
          draftState.isLoading = false;
        });
      })
      .addCase(deleteVehicleAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette kjøretøyet.");
      })
      .addCase(getVehicleAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getVehicleAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.vehicle = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getVehicleAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente kjøretøyet.");
      });
  },
});

export const { reducer } = vehicleSlice;

export default vehicleSlice;
